/**
 * The arithmetic of a refund, kept in one place and tested.
 *
 * Every number here has to agree with what admintgfront shows in
 * app/service/messages.py::get_refund_confirmation - the same payment must not read as two
 * different refunds depending on where an admin looked at it.
 */

import type { Payment, RefundPreparation } from "../api/types";

/** Balances are stored in hundredths of a token, clients and admins see tokens. */
export const TOKEN_CENTS = 100;

/** Kinds that raise a limit instead of crediting tokens. There is nothing to claw back there. */
export const LIMIT_KINDS = ["requests_limit", "chats_limit", "trackings_limit"] as const;

export function tokensOf(cents: number): number {
  return cents / TOKEN_CENTS;
}

export function isLimitPayment(payment: Payment): boolean {
  return (LIMIT_KINDS as readonly string[]).includes(payment.kind);
}

/**
 * Share of the package the client has already spent, in percent.
 *
 * Zero for a limit purchase: it credited no tokens, so nothing of it can be spent. Zero as
 * well for a package of zero tokens, which should not exist but must not produce a NaN on a
 * screen if it ever does.
 */
export function getShortfallPercent(preparation: RefundPreparation): number {
  const tokens = preparation.payment.tokens;
  if (!tokens) {
    return 0;
  }
  return Math.round((preparation.shortfall / tokens) * 100);
}

/**
 * Whether a refund can be turned down rather than only confirmed or cancelled.
 *
 * Only a request the client made, and only once - the same rule as can_reject in admintgfront
 * and as clientbackapi itself. A refund an admin started by hand has nobody waiting for an
 * answer, and a request already turned down has had its answer: rejecting either would message
 * the client about a decision that is not news to them.
 */
export function canReject(payment: Payment): boolean {
  return Boolean(payment.refund_requested_at) && !payment.refund_rejected_at;
}

/** Whether the confirmation should warn that most of the package is already gone. */
export function isShortfallWarned(preparation: RefundPreparation, warningPercent: number): boolean {
  return getShortfallPercent(preparation) > warningPercent;
}
